import React, { useEffect, useRef, useState } from 'react';
import { MdOutlineKeyboardDoubleArrowDown } from 'react-icons/md';
import { FcGoogle } from 'react-icons/fc';
import FeatureCarousel from '../components/FeatureCarousel';
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '../lib/firebase';
import { getStoredUserProfile, persistUserSession } from '../utils/authStorage';
import { isAdEnabled } from '../utils/adVisibility';
import useLocale from '../hooks/useLocale';
import { setLocalePreference } from '../utils/locale';
import AdMaxUnit from '../components/AdMaxUnit';
import Footer from '../components/Footer';
import '../styles/LoginPage.css';

const LOGIN_TEXT = {
  ja: {
    start: 'はじめる',
    loginLoading: 'ログイン中...',
    startWithGoogle: 'Googleではじめる',
    loginFailed: 'Googleログインに失敗しました。もう一度お試しください。',
    introTitle: 'Chord Analyzerとは',
    introLeadRows: [
      'Chord Analyzerは楽曲のコード分析・オリジナル曲の作曲を支援するツールです。',
      'コード進行を入力すると、キーに基づいてコードの区分やディグリーを色分け表示します。',
    ],
    mainPageNote: '作成したスコアを保存・管理することが可能です。',
    scorePageNote: 'キーを指定し、コードを入力することで自動でコード区分やディグリーを解析できます。',
    featuresTitle: '機能一覧',
    featurePoints: [
      {
        number: '01',
        title: 'コードの自動解析',
        description: 'キーに合わせて、コードの区分とディグリーを自動で色分け。進行の流れを確かめながら、次の響きを探せます。',
      },
      {
        number: '02',
        title: 'リアルタイム再生',
        description: 'コードをひとつずつ聴いたり、BPMに合わせて進行を再生したり。思い描いた響きを、その場で確かめられます。',
      },
      {
        number: '03',
        title: 'スコアの保存・整理',
        description: '進行を保存し、ハッシュタグやアーティスト別に整理。作りかけのアイデアも、すぐに見つかります。',
      },
      {
        number: '04',
        title: 'Excelライクなコード入力',
        description: 'セルにコードを入力し、Tabや矢印キーで次のセルへ。候補から選ぶ操作にも対応し、作曲の流れを止めません。',
      },
    ],
  },
  en: {
    start: 'Get Started',
    loginLoading: 'Signing in...',
    startWithGoogle: 'Continue with Google',
    loginFailed: 'Google sign-in failed. Please try again.',
    introTitle: 'About Chord Analyzer',
    introLeadRows: [
      'Chord Analyzer helps you analyze chord progressions and compose original music.',
      'Enter a progression to visualize chord functions and degrees based on the selected key.',
    ],
    mainPageNote: 'You can save and manage the scores you create.',
    scorePageNote: 'Choose a key and enter chords to automatically analyze chord function and degree.',
    featuresTitle: 'Features',
    featurePoints: [
      {
        number: '01',
        title: 'Automatic Chord Analysis',
        description: 'Chord functions and degrees are color-coded for quick reading.',
      },
      {
        number: '02',
        title: 'Real-Time Playback',
        description: 'Instantly hear the chords you enter.',
      },
      {
        number: '03',
        title: 'Save and Organize Scores',
        description: 'Save your progressions and organize them by artist and hashtag. Pick up an idea right where you left off.',
      },
      {
        number: '04',
        title: 'Excel-Like Chord Input',
        description: 'Input and edit chords with familiar spreadsheet-style controls.',
      },
    ],
  },
};

function LoginPage() {
  const locale = useLocale();
  const text = LOGIN_TEXT[locale] || LOGIN_TEXT.en;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const videoRef = useRef(null);
  const canShowAds = isAdEnabled();
  const localeToggleStyle = {
    position: 'fixed',
    top: '18px',
    right: '18px',
    zIndex: 30,
    width: '165px',
    minWidth: '165px',
  };

  const handlePrimaryAction = () => {
    const storedUser = getStoredUserProfile();
    window.location.href = storedUser ? '/' : '/?mode=compose';
  };

  const handleLocaleChange = (nextLocale) => {
    setLocalePreference(nextLocale);
  };

  const handleGoogleStart = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const credential = await signInWithPopup(auth, googleProvider);
      const normalizedUser = persistUserSession(credential.user);
      if (!normalizedUser) {
        throw new Error('Unable to resolve user profile after sign-in');
      }
      window.location.href = '/';
    } catch (error) {
      console.error('Login page sign-in failed:', error);
      window.alert(text.loginFailed);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const nodes = document.querySelectorAll('.reveal');
    if (!nodes.length) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.1 }
    );
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  const scrollToDetails = () => {
    const section = document.getElementById('login-details');
    section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const handleLoadedData = () => {
      setIsVideoReady(true);
      const promise = video.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch(() => {});
      }
    };

    const tryPlay = () => {
      const promise = video.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch(() => {});
      }
    };

    if (video.readyState >= 2) {
      setIsVideoReady(true);
      tryPlay();
    } else {
      video.addEventListener('loadeddata', handleLoadedData, { once: true });
    }

    return () => {
      video.removeEventListener('loadeddata', tryPlay);
      video.removeEventListener('loadeddata', handleLoadedData);
    };
  }, []);

  return (
    <div className="login-page">
      <div
        className="login-locale-toggle"
        role="group"
        aria-label="Language selector"
        style={localeToggleStyle}
      >
        <button
          type="button"
          className={`login-locale-button${locale === 'ja' ? ' is-active' : ''}`}
          onClick={() => handleLocaleChange('ja')}
        >
          日本語
        </button>
        <button
          type="button"
          className={`login-locale-button${locale === 'en' ? ' is-active' : ''}`}
          onClick={() => handleLocaleChange('en')}
        >
          English
        </button>
      </div>
      <div className="login-wrapper">
        <div className="left-section">
          <div className="login-ambient login-ambient--one" />
          <div className="login-ambient login-ambient--two" />
          <div className={`login-video-fallback${isVideoReady ? ' login-video-fallback--hidden' : ''}`} />
          <video
            ref={videoRef}
            className={`login-background-video${isVideoReady ? ' is-ready' : ''}`}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
          >
            <source src={`${process.env.PUBLIC_URL}/LoginMovie.mp4`} type="video/mp4" />
          </video>
          <div className="login-overlay-title">Chord Analyzer</div>
          <div className="login-overlay-subtitle">Visualize Your Chord Progressions</div>
        </div>
        <div className="right-section">
          <div className="login-right-content">
            <img src="/ChordAnalyzer.png" alt="Chord Analyzer logo" className="login-logo-image" />
            <div className="login-actions">
              <button onClick={handlePrimaryAction} className="login-primary-button" type="button">
                {text.start}
              </button>
              <button
                onClick={handleGoogleStart}
                className="login-google-button"
                type="button"
                disabled={isSubmitting}
              >
                <FcGoogle className="login-google-icon" aria-hidden="true" />
                <span>{isSubmitting ? text.loginLoading : text.startWithGoogle}</span>
              </button>
            </div>
          </div>
        </div>
        <button className="scroll-cue" type="button" aria-label="Scroll down" onClick={scrollToDetails}>
          <MdOutlineKeyboardDoubleArrowDown className="scroll-arrow" />
        </button>
      </div>
      <section id="login-details" className="login-intro">
        <div className="intro-inner reveal">
          <p className="intro-kicker">Overview</p>
          <h3 className="intro-title">{text.introTitle}</h3>
          <p className="intro-lead">
            {text.introLeadRows.map((row) => (
              <span key={row} className="intro-lead-row">{row}</span>
            ))}
          </p>
        </div>
        <div className="intro-mosaic">
          <div className="mosaic-card reveal mosaic-image-card mosaic-hero">
            <div className="mosaic-label">Main Page</div>
            <img src="/MainPage.png" alt="Main page overview" className="mosaic-image" />
            <p className="mosaic-note">{text.mainPageNote}</p>
          </div>
          <div className="mosaic-card reveal mosaic-image-card mosaic-hero">
            <div className="mosaic-label">Score Page</div>
            <img src="/ScorePage.png" alt="Score page overview" className="mosaic-image" />
            <p className="mosaic-note">{text.scorePageNote}</p>
          </div>
        </div>
      </section>
      <section id="login-features" className="login-features">
        <div className="section-head reveal">
          <p className="section-kicker">Features</p>
          <h3>{text.featuresTitle}</h3>
        </div>
        <FeatureCarousel items={text.featurePoints} locale={locale} />
      </section>
      {canShowAds && (
        <section className="login-ad-slot" aria-label="Advertisement">
          <AdMaxUnit />
        </section>
      )}
      <Footer />
    </div>
  );
}

export default LoginPage;
